import { ArrowRight, MessageCircle, Check } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { HERO } from "@/constants/testIds";

const HERO_BG =
  "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?crop=entropy&cs=srgb&fm=jpg&w=2000&q=80";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Hero = () => {
  const { t } = useLanguage();

  return (
    <section
      id="hero"
      className="relative min-h-[100svh] w-full overflow-hidden grain-overlay"
    >
      {/* Background image with heavy overlay for orthodox candlelight feel */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Young community gathering"
          className="w-full h-full object-cover"
        />
        {/* Dark anthracite gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(26,26,28,0.55) 0%, rgba(26,26,28,0.78) 60%, rgba(26,26,28,0.94) 100%)",
          }}
        />
        {/* Subtle radial gold glow (candlelight) */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 80% 20%, rgba(198,156,34,0.18) 0%, transparent 50%)",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative container-boe min-h-[100svh] flex flex-col justify-center py-32">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-4xl"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-gold/40 bg-white/5 backdrop-blur-md">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
            <span className="text-xs uppercase tracking-[0.3em] font-medium" style={{ color: "#E5C76B" }}>
              {t.hero.eyebrow}
            </span>
          </div>

          <h1 className="mt-8 text-white text-5xl sm:text-6xl lg:text-7xl xl:text-[5.75rem] font-bold leading-[1.04] tracking-tight">
            <span className="block">{t.hero.title1}</span>
            <span className="block font-accent italic font-medium" style={{ color: "#E5C76B" }}>
              {t.hero.title2}
            </span>
            <span className="block">{t.hero.title3}</span>
          </h1>

          <p className="mt-8 text-lg md:text-xl text-white/85 leading-relaxed max-w-2xl font-light">
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <button
              data-testid={HERO.joinBtn}
              onClick={() => scrollTo("contact")}
              className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold tracking-wide transition-all duration-300 ease-out"
              style={{ background: "#C69C22", color: "#1A1A1C" }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#E5C76B"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#C69C22"; }}
            >
              {t.hero.joinBtn}
              <ArrowRight size={18} />
            </button>
            <button
              data-testid={HERO.contactBtn}
              onClick={() => scrollTo("contact")}
              className="btn-ghost-light"
            >
              <MessageCircle size={18} />
              {t.hero.contactBtn}
            </button>
          </div>

          {/* Trust badges */}
          <div className="mt-14 flex flex-wrap gap-x-8 gap-y-3 max-w-2xl">
            {t.hero.trust.map((item, i) => (
              <div key={i} className="flex items-center gap-2.5 text-white/90">
                <span className="w-6 h-6 rounded-full border border-gold/50 flex items-center justify-center" style={{ background: "rgba(198,156,34,0.15)" }}>
                  <Check size={12} strokeWidth={3} style={{ color: "#E5C76B" }} />
                </span>
                <span className="text-sm font-medium">{item}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-white/50">
        <span className="text-[10px] uppercase tracking-[0.3em]">scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-gold/60 to-transparent" />
      </div>
    </section>
  );
};

export default Hero;
